import { AfterViewInit, Component, input, ViewChild, OnChanges } from '@angular/core';
import { MatButtonToggleChange, MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { PlayExecutionInteractionErrorTableData } from '@r1-compass-library/services/reporting/play-execution-interaction-error-table.service';

export type InteractionTableType = 'database' | 'processing' | 'salesforce';

export type DatabaseErrorTableRow = {
    query?: string;
    status?: string;
    numErroredRecords?: string;
    rawException?: string;
};

export type ProcessingErrorTableRow = {
    numErroredRecords: string;
    rawException: string;
};

export type SalesforceErrorTableRow = {
    statusCode: string;
    numErroredRecords: string;
    rawException: string;
};

@Component({
    selector: 'r1-compass-lib-interaction-error-table',
    templateUrl: './interaction-error-table.component.html',
    styleUrls: ['./interaction-error-table.component.scss'],
    imports: [MatButtonToggleModule, MatTableModule, MatPaginatorModule, MatSortModule],
})
export class InteractionErrorTableComponent implements AfterViewInit, OnChanges {
    // The combination of ViewChild and setter allows us to set the paginator/sort
    // as soon as they are available in the DOM, or when they are destroyed (and become undefined).
    @ViewChild('databasePaginator') set databasePaginator(paginator: MatPaginator | undefined) {
        if (!paginator) {
            return;
        }
        this.databaseInteractionDataSource.paginator = paginator;
        this.databaseInteractionDataSource.paginator.firstPage();
    }
    @ViewChild('databaseSort') set databaseSort(sort: MatSort | undefined) {
        if (!sort) {
            return;
        }
        this.databaseInteractionDataSource.sort = sort;
    }
    @ViewChild('processingPaginator') set processingPaginator(paginator: MatPaginator | undefined) {
        if (!paginator) {
            return;
        }
        this.processingErrorDataSource.paginator = paginator;
        this.processingErrorDataSource.paginator.firstPage();
    }
    @ViewChild('processingSort') set processingSort(sort: MatSort | undefined) {
        if (!sort) {
            return;
        }
        this.processingErrorDataSource.sort = sort;
    }
    @ViewChild('salesforcePaginator') set salesforcePaginator(paginator: MatPaginator | undefined) {
        if (!paginator) {
            return;
        }
        this.salesforceInteractionDataSource.paginator = paginator;
        this.salesforceInteractionDataSource.paginator.firstPage();
    }
    @ViewChild('salesforceSort') set salesforceSort(sort: MatSort | undefined) {
        if (!sort) {
            return;
        }
        this.salesforceInteractionDataSource.sort = sort;
    }

    readonly interactionErrorTableData = input<PlayExecutionInteractionErrorTableData | null>(null);

    selectedInteractionTable: InteractionTableType | null = null;
    interactionPageSize = 10;

    databaseInteractionDataSource = new MatTableDataSource<DatabaseErrorTableRow>([]);
    salesforceInteractionDataSource = new MatTableDataSource<SalesforceErrorTableRow>([]);
    processingErrorDataSource = new MatTableDataSource<ProcessingErrorTableRow>([]);

    databaseColumnsToDisplay: string[] = ['numErroredRecords', 'status', 'query', 'rawException'];
    salesforceColumnsToDisplay: string[] = ['numErroredRecords', 'statusCode', 'rawException'];
    processingErrorColumnsToDisplay: string[] = ['numErroredRecords', 'rawException'];

    onInteractionTableChange(change: MatButtonToggleChange) {
        this.selectedInteractionTable = change.value;
    }

    ngOnChanges() {
        const databaseErrorData = this.interactionErrorTableData()?.databaseErrorTableData;
        if (databaseErrorData) {
            this.databaseInteractionDataSource = new MatTableDataSource<DatabaseErrorTableRow>(databaseErrorData);
        }

        const processingErrorData = this.interactionErrorTableData()?.processingErrorTableData;
        if (processingErrorData) {
            this.processingErrorDataSource = new MatTableDataSource<ProcessingErrorTableRow>(processingErrorData);
        }

        const salesforceErrorData = this.interactionErrorTableData()?.salesforceErrorTableData;
        if (salesforceErrorData) {
            this.salesforceInteractionDataSource = new MatTableDataSource<SalesforceErrorTableRow>(salesforceErrorData);
        }
    }

    ngAfterViewInit() {
        this.salesforceInteractionDataSource.paginator = this.salesforcePaginator;
        this.salesforceInteractionDataSource.sort = this.salesforceSort;

        this.databaseInteractionDataSource.paginator = this.databasePaginator;
        this.databaseInteractionDataSource.sort = this.databaseSort;
    }
}
